import * as THREE from 'three';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { HUD } from '../ui/hud.js';
import '../ui/space.css';

// Deep Space — a small asteroid beyond the moon where Gary's repaired ship can fly.
// Reward-free chill zone with a stellar view and a quick return to the park.

const VOID = 0x0A0514;

export function createDeepSpaceScene(){ return () => new DeepSpaceScene(); }

class DeepSpaceScene {
  constructor(){
    this.panels = [];
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(VOID);
    this.scene.fog = new THREE.Fog(VOID, 18, 55);

    this.camera = new THREE.PerspectiveCamera(50, window.innerWidth / window.innerHeight, 0.1, 120);
    this.camDist = 16; this.camTarget = new THREE.Vector3(0, 1.4, 0);
    this.azimuth = 0; this.polar = 0.5;

    this._buildLights();
    this._buildAsteroid();
    this._buildShip();
    this._buildStars();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 4);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.hotspots = [
      { pos: new THREE.Vector3(0, 0, -3.5), r: 2.6, label: '🚀 board Gary\'s ship', action: () => this.manager.goTo('park', { from: 'deepSpace' }) },
      { pos: new THREE.Vector3(-5, 0, -5), r: 2.8, label: '✨ fly onward to the Crovian System', action: () => this.manager.goTo('crovian', { from: 'deepSpace' }) }
    ];
    this.nearSpot = null;
  }

  _buildLights(){
    this.scene.add(new THREE.AmbientLight(0xA090C0, 0.35));
    const sun = new THREE.DirectionalLight(0xE0D8FF, 0.55);
    sun.position.set(8, 12, 6); sun.castShadow = true;
    sun.shadow.mapSize.set(1024, 1024);
    const s = 18;
    sun.shadow.camera.left = -s; sun.shadow.camera.right = s;
    sun.shadow.camera.top = s; sun.shadow.camera.bottom = -s;
    this.scene.add(sun);
    const glow = new THREE.PointLight(0x88CCFF, 0.4, 16, 2);
    glow.position.set(-3, 2, -3); this.scene.add(glow);
  }

  _buildAsteroid(){
    const g = new THREE.Group(); this.scene.add(g);
    // lumpy rock platform
    const rock = new THREE.Mesh(
      new THREE.SphereGeometry(6, 24, 16),
      new THREE.MeshLambertMaterial({ color: 0x5A4A6A })
    );
    rock.position.y = -5.4; rock.scale.set(1, 0.45, 1); rock.receiveShadow = true; g.add(rock);
    // a few craters / boulders
    for (let i = 0; i < 6; i++){
      const a = i / 6 * Math.PI * 2;
      const r = 2.2 + Math.random() * 1.2;
      const s = 0.35 + Math.random() * 0.25;
      const b = new THREE.Mesh(
        new THREE.SphereGeometry(s, 10, 8),
        new THREE.MeshLambertMaterial({ color: 0x4A3A5A })
      );
      b.position.set(Math.cos(a) * r, 0.1, Math.sin(a) * r);
      b.scale.y = 0.6; b.castShadow = true; g.add(b);
    }
    const sign = textSprite('Deep Space', 24, '#EAE0FF', 3.6, 0.9);
    sign.position.set(0, 4.2, -6); this.scene.add(sign);
  }

  _buildShip(){
    const g = new THREE.Group(); g.position.set(0, 0.2, -3.2); this.scene.add(g);
    cyl(1.0, 1.4, 0.45, 0x7A8A9A, 0, 0.25, 0, g, 20);
    cyl(0.6, 0.9, 0.55, 0x9AA8B8, 0, 0.65, 0, g, 20);
    sphere(0.48, 0xA0D0E0, 0, 1.1, 0, g, 16);
    // engine glow
    const glow = new THREE.PointLight(0x66CCFF, 0.8, 8, 2);
    glow.position.set(0, 0.6, 0.8); g.add(glow);
  }

  _buildStars(){
    const geo = new THREE.BufferGeometry();
    const verts = [];
    for (let i = 0; i < 300; i++){
      const r = 40 + Math.random() * 60;
      const t = Math.random() * Math.PI * 2;
      const p = (Math.random() - 0.5) * Math.PI;
      verts.push(
        Math.cos(t) * Math.cos(p) * r,
        Math.sin(p) * r,
        Math.sin(t) * Math.cos(p) * r
      );
    }
    geo.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
    const mat = new THREE.PointsMaterial({ color: 0xFFFFFF, size: 0.25, sizeAttenuation: true, transparent: true, opacity: 0.85 });
    this.scene.add(new THREE.Points(geo, mat));
  }

  // ---- lifecycle ----
  enter({ manager }){
    this.manager = manager; this.input = manager.input;
    this.input.useCamera(this.camera); this.input.polarRange = [0.25, 0.7];
    this.input.onTap = (ndc, ray) => this._tap(ray);
    HUD.setSubtitle('deep space');
    HUD.say('The stars go on forever out here. Gary\'s ship can take you back when you\'re ready.');
    HUD.setHint('click to walk · board the ship to return to Toast Park · fly onward to the Crovian System');
    HUD.hideHintSoon(8000);
  }

  exit(){ this.input.onTap = null; HUD.showEnter(null); HUD.say(''); this._clearPanels(); }

  _tap(ray){
    const dir = ray.ray.direction; if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y; if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(Math.max(-7, Math.min(7, p.x)), 0, Math.max(-6, Math.min(6, p.z)));
    HUD.hideHint(); this.bread.walkTo(gp);
  }

  update(dt){
    this.bread.update(dt);

    let near = null, nd = Infinity;
    this.hotspots.forEach(h => {
      if (h.hidden || h.interactive === false) return;
      const d = h.pos.distanceTo(this.bread.root.position);
      if (d < nd){ nd = d; near = h; }
    });
    if (near && nd < near.r){
      if (this.nearSpot !== near){ this.nearSpot = near; HUD.showEnter(near.label, () => near.action()); }
    } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.5, 1.4, this.bread.root.position.z * 0.5), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){ this.camera.aspect = window.innerWidth / window.innerHeight; this.camera.updateProjectionMatrix(); }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }
}
